'use client';

import { createContext, useContext, useState, ReactNode } from 'react';
import { useLocation } from 'react-router';
import { isBusinessAppPath } from '@/lib/business-app-path';

export interface AccountType {
  id: string;
  title: string;
  description: string;
  type: 'checking' | 'savings' | 'cd' | 'business';
  features: string[];
  productFeatures?: {
    impactCheckingDonationSelection?: boolean;
  };
  // Extended product fields from MongoDB (all configurable via admin)
  apy?: number;                          // Float - APY percentage
  minimumDeposit?: number;               // Float - Minimum deposit amount
  fundingMethodsAllowed?: string;        // String - Comma-separated: 'Wire, Check, Plaid'
  productHeadline?: string;              // String - Headline shown in cart
  productCardDescriptionHtml?: string;   // String - Full HTML for product card
  finePrintHtml?: string;                // String - Disclosure HTML
  accountTermsUrl?: string;              // String - URL to terms PDF
  accountType?: string;                  // String - 'CK', 'SV', or 'CD'
  interestRate?: number;                 // Float - Interest rate percentage
}

export interface CartItem {
  accountType: AccountType;
  quantity: number;
}

interface AccountCartContextType {
  cart: CartItem[];
  addToCart: (accountType: AccountType) => void;
  removeFromCart: (accountTypeId: string) => void;
  updateQuantity: (accountTypeId: string, quantity: number) => void;
  clearCart: () => void;
  getCartTotal: () => number;
  hasAccountType: (accountTypeId: string) => boolean;
  hasAccountTypeByCategory: (type: 'checking' | 'savings' | 'cd' | 'business') => boolean;
  canAddAccountType: (accountType: AccountType) => boolean;
  selectAccount: (accountType: AccountType) => void;
}

const AccountCartContext = createContext<AccountCartContextType | null>(null);

export const useAccountCart = () => {
  const context = useContext(AccountCartContext);
  if (!context) {
    throw new Error('useAccountCart must be used within an AccountCartProvider');
  }
  return context;
};

interface AccountCartProviderProps {
  children: ReactNode;
}

export const AccountCartProvider = ({ children }: AccountCartProviderProps) => {
  const location = useLocation();
  const isBao = isBusinessAppPath(location.pathname);

  const [baoCart, setBaoCart] = useState<CartItem[]>([]);
  const [caoCart, setCaoCart] = useState<CartItem[]>([]);

  const cart = isBao ? baoCart : caoCart;
  const setCart = isBao ? setBaoCart : setCaoCart;

  // Dispatch a global event so UI elements (e.g., cart toggle button) can animate consistently
  const dispatchCartPing = (
    action: 'add' | 'remove' | 'update' | 'clear',
    accountType?: AccountType
  ) => {
    if (typeof window === 'undefined') {return;}
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const w = window as any;
    if (typeof w.dispatchEvent === 'function' && typeof CustomEvent !== 'undefined') {
      w.dispatchEvent(
        new CustomEvent('cart-ping', {
          detail: { action, title: accountType?.title },
        })
      );
    }
  };

  const addToCart = (accountType: AccountType) => {
    const wasInCart = cart.some(item => item.accountType.id === accountType.id);
    setCart(prev => {
      const existingItem = prev.find(item => item.accountType.id === accountType.id);
      if (existingItem) {
        // Increment quantity when adding an already-selected account
        return prev.map(item =>
          item.accountType.id === accountType.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, { accountType, quantity: 1 }];
    });
    dispatchCartPing(wasInCart ? 'update' : 'add', accountType);
  };

  const removeFromCart = (accountTypeId: string) => {
    const found = cart.find(item => item.accountType.id === accountTypeId)?.accountType;
    setCart(prev => prev.filter(item => item.accountType.id !== accountTypeId));
    dispatchCartPing('remove', found);
  };

  const updateQuantity = (accountTypeId: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(accountTypeId);
      return;
    }
    const found = cart.find(item => item.accountType.id === accountTypeId)?.accountType;
    setCart(prev =>
      prev.map(item =>
        item.accountType.id === accountTypeId
          ? { ...item, quantity }
          : item
      )
    );
    dispatchCartPing('update', found);
  };

  const clearCart = () => {
    setCart([]);
    dispatchCartPing('clear');
  };

  const getCartTotal = () => {
    return cart.reduce((sum, item) => sum + (item.quantity || 0), 0);
  };

  const hasAccountType = (accountTypeId: string) => {
    return cart.some(item => item.accountType.id === accountTypeId);
  };

  const hasAccountTypeByCategory = (type: 'checking' | 'savings' | 'cd' | 'business') => {
    return cart.some(item => item.accountType.type === type);
  };

  const canAddAccountType = (accountType: AccountType) => {
    return !hasAccountTypeByCategory(accountType.type);
  };

  // Atomically select an account for a given type. If the same account is clicked again, toggle it off.
  const selectAccount = (accountType: AccountType) => {
    const wasInCart = cart.some(item => item.accountType.id === accountType.id);
    setCart(prev => {
      const existingById = prev.find(item => item.accountType.id === accountType.id);
      if (existingById) {
        // If already selected, increment quantity via select action
        return prev.map(item =>
          item.accountType.id === accountType.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      // Add alongside others with initial quantity 1
      return [...prev, { accountType, quantity: 1 }];
    });
    dispatchCartPing(wasInCart ? 'update' : 'add', accountType);
  };

  const contextValue: AccountCartContextType = {
    cart,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartTotal,
    hasAccountType,
    hasAccountTypeByCategory,
    canAddAccountType,
    selectAccount,
  };

  return (
    <AccountCartContext.Provider value={contextValue}>
      {children}
    </AccountCartContext.Provider>
  );
};
